/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {setDeepLinkRestriction} from '../../store/actions/nav';
import {deepLinkRestrictedSelector} from '../../store/selectors/nav-selectors';

/**
 * useDeepLinkValidation is a custom hook used to validate deep link navigation.
 * It checks if there is a deep link error, and if so, it clears the error and returns false.
 * Otherwise, it returns true, indicating that navigation is valid.
 *
 * ```typescript
 *
 *  import {useDeepLinkValidation} from './useDeepLinkValidation';
 *
 *  const {preValidator, clearError} = useDeepLinkValidation();
 *
 * ```
 *
 * @returns - Returns false if there's a deep link error, true if navigation is valid.
 */
export const useDeepLinkValidation = () => {
  const dispatch           = useDispatch();
  const deepLinkRestricted = useSelector(deepLinkRestrictedSelector);

  const clearError = useCallback(() => {
    // Clear the deep link error if it exists
    dispatch(setDeepLinkRestriction(false));
  }, []);

  const preValidator = useCallback((clear = true) => {
    if (deepLinkRestricted && clear) {
      clearError();
    }
    return !deepLinkRestricted;
  }, [deepLinkRestricted]);

  return {preValidator, clearError};
};

export default useDeepLinkValidation;
